import { challengeTypes } from '@freecodecamp/shared/config/challenge-types';
import { concatHtml } from './builders.js';
import { getTransformers, embedFilesInHtml, getPythonTransformers, getMultifileJSXTransformers } from './transformers.js';
import { setupTSCompiler } from './typescript-worker-handler.js';
const applyFunction = (fn) => async (file) => {
    try {
        if (file.error) {
            return file;
        }
        const newFile = await fn.call(this, file);
        if (typeof newFile !== 'undefined') {
            return newFile;
        }
        return file;
    }
    catch (error) {
        return { ...file, error };
    }
};
const composeFunctions = (...fns) => fns.map(applyFunction).reduce((f, g) => x => f(x).then(g));
// Test helpers parse sources.contents across all files. Without this boundary,
// an index.ts first-line declaration can be concatenated onto empty/no-newline
// HTML or CSS, so AST checks fail.
const joinWithFileBoundaries = (contents) => contents.join('\n');
function buildSourceMap(challengeFiles) {
    // TODO: rename sources.index to sources.contents.
    const index = joinWithFileBoundaries(challengeFiles.map(challengeFile => challengeFile.source ?? ''));
    const editableContents = joinWithFileBoundaries(challengeFiles.map(challengeFile => challengeFile.editableContents ?? ''));
    const source = challengeFiles.length
        ? {
            index,
            contents: index,
            editableContents
        }
        : {
            index,
            editableContents
        };
    return source;
}
const buildFunctions = {
    [challengeTypes.js]: buildJSChallenge,
    [challengeTypes.jsProject]: buildJSChallenge,
    [challengeTypes.html]: buildDOMChallenge,
    [challengeTypes.modern]: buildDOMChallenge,
    [challengeTypes.backend]: buildBackendChallenge,
    [challengeTypes.backEndProject]: buildBackendChallenge,
    [challengeTypes.pythonProject]: buildBackendChallenge,
    [challengeTypes.multifileCertProject]: buildDOMChallenge,
    [challengeTypes.colab]: buildBackendChallenge,
    [challengeTypes.python]: buildPythonChallenge,
    [challengeTypes.multifilePythonCertProject]: buildPythonChallenge,
    [challengeTypes.lab]: buildDOMChallenge,
    [challengeTypes.jsLab]: buildJSChallenge,
    [challengeTypes.pyLab]: buildPythonChallenge,
    [challengeTypes.dailyChallengeJs]: buildJSChallenge,
    [challengeTypes.dailyChallengePy]: buildPythonChallenge
};
export function canBuildChallenge(challengeData) {
    const { challengeType } = challengeData;
    return Object.prototype.hasOwnProperty.call(buildFunctions, challengeType);
}
export async function buildChallenge(challengeData, options) {
    const { challengeType } = challengeData;
    const build = buildFunctions[challengeType];
    if (build) {
        return build(challengeData, options);
    }
    throw new Error(`Cannot build challenge of type ${challengeType}`);
}
export const prefixDoctype = ({ build, sources }) => {
    // DOCTYPE should be the first thing written to the frame, so if the user code
    // includes a DOCTYPE declaration, we need to find it and write it first.
    const doctype = sources.contents?.match(/^<!DOCTYPE html>/i)?.[0] || '';
    return doctype + build;
};
export const runnerTypes = {
    [challengeTypes.html]: 'dom',
    [challengeTypes.js]: 'javascript',
    [challengeTypes.backend]: 'dom',
    [challengeTypes.zipline]: 'dom',
    [challengeTypes.frontEndProject]: 'dom',
    [challengeTypes.backEndProject]: 'dom',
    [challengeTypes.pythonProject]: 'python',
    [challengeTypes.jsProject]: 'javascript',
    [challengeTypes.modern]: 'dom',
    [challengeTypes.step]: 'dom',
    [challengeTypes.quiz]: 'dom',
    [challengeTypes.invalid]: 'dom',
    [challengeTypes.video]: 'dom',
    [challengeTypes.codeAllyPractice]: 'dom',
    [challengeTypes.codeAllyCert]: 'dom',
    [challengeTypes.multifileCertProject]: 'dom',
    [challengeTypes.theOdinProject]: 'dom',
    [challengeTypes.colab]: 'dom',
    [challengeTypes.exam]: 'dom',
    [challengeTypes.msTrophy]: 'dom',
    [challengeTypes.multipleChoice]: 'dom',
    [challengeTypes.python]: 'python',
    [challengeTypes.dialogue]: 'dom',
    [challengeTypes.fillInTheBlank]: 'dom',
    [challengeTypes.multifilePythonCertProject]: 'python',
    [challengeTypes.generic]: 'dom',
    [challengeTypes.lab]: 'dom',
    [challengeTypes.jsLab]: 'javascript',
    [challengeTypes.pyLab]: 'python',
    [challengeTypes.dailyChallengeJs]: 'javascript',
    [challengeTypes.dailyChallengePy]: 'python',
    [challengeTypes.review]: 'dom',
    [challengeTypes.freeCodeCampOsPractice]: 'dom',
    [challengeTypes.freeCodeCampOsCert]: 'dom'
};
function hasTS(challengeFiles) {
    return challengeFiles.some(challengeFile => challengeFile.ext === 'ts' || challengeFile.ext === 'tsx');
}
const isTSConfig = (f) => f.name === 'tsconfig' && f.ext === 'json';
export function getTSConfig(challengeFiles) {
    const tsConfigFiles = challengeFiles.filter(isTSConfig);
    if (tsConfigFiles.length > 1) {
        throw new Error('TypeScript challenge must include only one tsconfig.json file');
    }
    return tsConfigFiles.length === 1 ? tsConfigFiles[0].contents : null;
}
async function configureTSCompiler(challengeFiles) {
    if (hasTS(challengeFiles)) {
        const tsConfig = getTSConfig(challengeFiles);
        if (tsConfig) {
            await setupTSCompiler(tsConfig);
        }
        else {
            await setupTSCompiler();
        }
    }
}
// TODO: All the buildXChallenge files have a similar structure, so make that
// abstraction (function, class, whatever) and then create the various functions
// out of it.
async function buildDOMChallenge({ challengeFiles, required = [], template = '', challengeType }, options) {
    // TODO: make this required in the schema.
    if (!challengeFiles)
        throw Error('No challenge files provided');
    const hasJsx = challengeFiles.some(challengeFile => challengeFile.ext === 'jsx' || challengeFile.ext === 'tsx');
    await configureTSCompiler(challengeFiles);
    const sourceFiles = challengeFiles.filter(file => !isTSConfig(file));
    const isMultifile = sourceFiles.length > 1;
    // I'm reasonably sure this is fine, but we need to migrate transformers to
    // TypeScript to be sure.
    const transformers = (isMultifile && hasJsx
        ? getMultifileJSXTransformers(options)
        : getTransformers(options));
    const pipeLine = composeFunctions(...transformers);
    const finalFiles = await Promise.all(sourceFiles.map(pipeLine));
    const error = finalFiles.find(({ error }) => error)?.error;
    const contents = (await embedFilesInHtml(finalFiles));
    // if there is an error, we just build the test runner so that it can be
    // used to run tests against the code without actually running the code.
    const toBuild = error
        ? {}
        : {
            required,
            template,
            contents
        };
    const requiresReact16 = required.some(({ src }) => src?.includes('https://cdnjs.cloudflare.com/ajax/libs/react/16.'));
    return {
        challengeType,
        build: concatHtml(toBuild),
        sources: buildSourceMap(finalFiles),
        loadEnzyme: requiresReact16,
        error
    };
}
async function buildJSChallenge({ challengeFiles, challengeType }, options) {
    if (!challengeFiles)
        throw Error('No challenge files provided');
    const pipeLine = composeFunctions(...getTransformers(options));
    await configureTSCompiler(challengeFiles);
    const sourceFiles = challengeFiles.filter(file => !isTSConfig(file));
    const finalFiles = await Promise.all(sourceFiles?.map(pipeLine));
    const error = finalFiles.find(({ error }) => error)?.error;
    const toBuild = error ? [] : finalFiles;
    return {
        challengeType,
        build: toBuild
            .reduce((body, challengeFile) => [...body, challengeFile.contents], [])
            .join('\n'),
        sources: buildSourceMap(finalFiles),
        error
    };
}
function buildBackendChallenge({ url, challengeType }) {
    return {
        challengeType,
        build: '',
        sources: { contents: url }
    };
}
async function buildPythonChallenge({ challengeFiles, challengeType }) {
    if (!challengeFiles)
        throw new Error('No challenge files provided');
    const pipeLine = composeFunctions(...getPythonTransformers());
    const finalFiles = await Promise.all(challengeFiles.map(pipeLine));
    const error = finalFiles.find(({ error }) => error)?.error;
    const sources = buildSourceMap(finalFiles);
    return {
        challengeType,
        sources,
        build: sources?.contents,
        error
    };
}
